import { AdminTeams } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import {
  AdminTeam,
  AdminTeamAssignedProject,
  AdminTeamMember,
  AdminTeamMemberSkill,
  UpdateTeamAssignmentRequestOrganizationDecision,
  UserTeamAssignmentStatusEnum,
} from '@platform/client/schemas';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { AdminActionsButtonProps, AdminActionsContainer } from '../../AdminListPage';
import { ModalCallToAction, PillBadge } from '@platform/components';
import { GenerateTeamsCta } from './GenerateTeamsCta';
import { ApproveTeamModal } from './modals/ApproveTeamModal';
import { DeleteTeamModal } from './modals/DeleteTeamModal';
import { ProjectAssignmentModal } from './modals/ProjectAssignmentModal';
import { Link } from 'wouter';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { ProjectLink } from '../projects/ProjectsAdmin';

const columnHelper = createColumnHelper<AdminTeam>();

export const TeamsResultsTab = ({
  cohortId,
  setCreateTeamModalOpen,
  setEditMembersModalOpen,
}: {
  cohortId: number;
  setCreateTeamModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      inComingMembers: undefined | AdminTeamMember[];
    }>
  >;
  setEditMembersModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      teamId: undefined | number;
      incomingNewMembers: undefined | AdminTeamMember[];
    }>
  >;
}) => {
  const [teamsGenerated, setTeamsGenerated] = useState(false);
  const [approveTeamIds, setApproveTeamIds] = useState<number[]>();
  const [deleteTeamId, setDeleteTeamId] = useState<number>();
  const [updateProjectData, setUpdateProjectData] = useState<{
    teamId: number;
    projectId?: number;
    currentOrganizationDecision?: 'pending' | 'accepted' | 'declined';
  }>();
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);

  const {
    data: teamsData,
    isLoading,
    isError,
    isSuccess,
  } = AdminTeams.useGetTeams(
    {
      cohort_id: cohortId,
      page: adminQueryOptionsV2.page,
      limit: adminQueryOptionsV2.limit,
    },
    { query: { placeholderData: keepPreviousData } },
  );

  const proposedTeamIds = teamsData?.teams
    .filter((team) => team.status === UserTeamAssignmentStatusEnum.proposed)
    .map((team) => team.id);
  const tableActions = useMemo(() => {
    const actions: ModalCallToAction[] = [
      {
        text: 'Create team',
        callback: () => setCreateTeamModalOpen({ isOpen: true, inComingMembers: undefined }),
      },
    ];
    if (proposedTeamIds && proposedTeamIds.length > 0) {
      actions.unshift({
        text: 'Approve all proposed',
        callback: () => setApproveTeamIds(proposedTeamIds),
      });
    }
    return actions;
  }, [proposedTeamIds, setCreateTeamModalOpen]);
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'ID',
          cell: ({ row }) => <TeamLink teamId={row.original.id} cohortId={cohortId} />,
          size: 10,
          enableColumnFilter: false,
          enableSorting: false,
        }),
        columnHelper.display({
          id: 'memberSkills',
          header: 'Members',
          cell: ({ row }) => <MembersSkillsCell row={row} />,
        }),
        columnHelper.accessor('categories', {
          header: 'Categories',
          cell: (info) => {
            const categories: string[] = info.getValue();
            return categories.map((category) => {
              return (
                <PillBadge variant='plain' size='sm' color='blue'>
                  {category}
                </PillBadge>
              );
            });
          },
        }),
        columnHelper.accessor('assignedProject', {
          header: 'Assigned Projects',
          cell: (info) => <AssignedProjectCell project={info.getValue() ?? undefined} />,
          size: 150,
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <TeamsActions
              row={row}
              setApproveTeamIds={setApproveTeamIds}
              setEditMembersModalOpen={setEditMembersModalOpen}
              setDeleteTeamId={setDeleteTeamId}
              setUpdateProjectData={setUpdateProjectData}
            />
          ),
          size: 10,
        }),
      ] as ColumnDef<AdminTeam>[],
    [setEditMembersModalOpen, cohortId],
  );

  return (
    <>
      {!teamsGenerated && isSuccess && teamsData?.teams.length === 0 && (
        <GenerateTeamsCta cohortId={cohortId} setTeamsGenerated={setTeamsGenerated} />
      )}
      {teamsGenerated && isSuccess && teamsData?.teams.length === 0 && (
        <p className='text-center mt-20 text-gray-700 text-xl'>
          Unable to form teams with current cohort. Teams must be created manually from free agents.
        </p>
      )}
      {isLoading ||
        isError ||
        (isSuccess && teamsData.teams?.length > 0 && (
          <div className='mt-2'>
            <AdminTable
              columns={columns}
              filteredCount={teamsData?.total || 0}
              data={teamsData?.teams || []}
              isError={isError}
              isLoading={isLoading}
              isSuccess={isSuccess}
              tableActions={tableActions}
            />
            {approveTeamIds && (
              <ApproveTeamModal
                isOpen={!!approveTeamIds}
                onClose={() => setApproveTeamIds(undefined)}
                cohortId={cohortId}
                teamIds={approveTeamIds}
              />
            )}

            {deleteTeamId && (
              <DeleteTeamModal
                teamId={deleteTeamId}
                cohortId={cohortId}
                onClose={() => setDeleteTeamId(undefined)}
              />
            )}
            {updateProjectData && (
              <ProjectAssignmentModal
                teamId={updateProjectData.teamId}
                currentProjectId={updateProjectData.projectId}
                cohortId={cohortId}
                onClose={() => setUpdateProjectData(undefined)}
                currentOrganizationDecision={updateProjectData.currentOrganizationDecision}
              />
            )}
          </div>
        ))}
    </>
  );
};

export const TeamLink = ({ teamId, cohortId }: { teamId: number; cohortId: number }) => {
  return (
    <Link
      to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.TEAMS}/${cohortId}/${teamId}`}
      className='text-brand-500 hover:underline'
    >
      {teamId}
    </Link>
  );
};

export const MembersSkillsCell = ({ row }: { row: Row<AdminTeam> }) => {
  return (
    <div className='flex flex-col gap-2'>
      {row.original.members.map((member) => (
        <div key={member.id}>
          <p className='font-medium'>
            <ProfessionalLink userId={member.id} name={member.name} />
          </p>
          <div className='text-xs'>
            <MemberSkillsChunked skills={member.skills} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const MemberSkillsChunked = ({ skills }: { skills: AdminTeamMemberSkill[] }) => {
  const [showAll, setShowAll] = useState(false);
  const firstThree = skills.slice(0, 3);
  const remaining = skills.slice(3);
  return (
    <div>
      {firstThree.map((skill, index) => (
        <p key={index}>
          {skill.skill} {skill.proficiency ? `(${skill.proficiency})` : ''}
        </p>
      ))}
      {!showAll && remaining.length > 0 && (
        <button className='text-xs blue-link mt-1' onClick={() => setShowAll(true)}>
          +{remaining.length} more
        </button>
      )}
      {showAll && (
        <>
          {remaining.map((skill, index) => (
            <p key={index}>
              {skill.skill} {skill.proficiency ? `(${skill.proficiency})` : ''}
            </p>
          ))}
          <button className='text-xs blue-link mt-1' onClick={() => setShowAll(false)}>
            Show less
          </button>
        </>
      )}
    </div>
  );
};

const AssignedProjectCell = ({ project }: { project?: AdminTeamAssignedProject }) => {
  if (!project) {
    return <p className='text-xs text-gray-500'>No project assigned</p>;
  }
  return (
    <>
      <ProjectLink projectId={project.id} projectName={project.name} />
      <p className='text-xs'>
        ({project.status.charAt(0).toUpperCase() + project.status.slice(1)}:{' '}
        {formatDateStr(project.statusDate)})
      </p>
    </>
  );
};

const TeamsActions = ({
  row,
  setApproveTeamIds,
  setEditMembersModalOpen,
  setDeleteTeamId,
  setUpdateProjectData,
}: {
  row: Row<AdminTeam>;
  setApproveTeamIds: React.Dispatch<React.SetStateAction<number[] | undefined>>;
  setEditMembersModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      teamId: undefined | number;
      incomingNewMembers: undefined | AdminTeamMember[];
    }>
  >;
  setDeleteTeamId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setUpdateProjectData: React.Dispatch<
    React.SetStateAction<
      | {
          teamId: number;
          projectId?: number;
          currentOrganizationDecision?: UpdateTeamAssignmentRequestOrganizationDecision;
        }
      | undefined
    >
  >;
}) => {
  const actions: AdminActionsButtonProps[] = [
    {
      text: 'Edit members',
      type: 'button',
      onPress: () =>
        setEditMembersModalOpen({
          isOpen: true,
          teamId: row.original.id,
          incomingNewMembers: undefined,
        }),
    },
    {
      text: 'Delete team',
      type: 'button',
      linkColor: 'red',
      onPress: () => setDeleteTeamId(row.original.id),
    },
  ];
  if (row.original.status === UserTeamAssignmentStatusEnum.proposed) {
    actions.unshift({
      text: 'Approve team',
      type: 'button',
      onPress: () => setApproveTeamIds([row.original.id]),
    });
  }
  if (row.original.status === UserTeamAssignmentStatusEnum.approved) {
    actions.unshift({
      text: 'Project assignment',
      type: 'button',
      onPress: () =>
        setUpdateProjectData({
          teamId: row.original.id,
          projectId: row.original.assignedProject?.id,
          currentOrganizationDecision: row.original.assignedProject
            ?.status as UpdateTeamAssignmentRequestOrganizationDecision,
        }),
    });
  }
  return <AdminActionsContainer actions={actions} />;
};
